import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { createSelector, select, Store } from '@ngrx/store';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { ConfigurableFormComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { OnFieldChanged } from '@ordersnproducts/modules/shared/modules/configurable-form/models/on-field-changed';
import { filter, map, take } from 'rxjs/operators';
import { LoadAddedValuesNotFormulatingAction } from '../../../actions/added-values.actions';
import { generateAddedValueFormName } from '../../../helpers/form-added-value-name-generator';
import { AddedValueFieldConfig } from '../../../models/form-added-value-field-config';
import { productAddedValues } from '../../../selectors/added-value.selectors';
import { getRmsRehydrate } from '../../../selectors';
import { RmsSpecificationRemoveRehydrateItem } from '../../../actions/rms-specification.actions';
import { getContractsBalance } from '../../../selectors/contracts.selectors';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';

const balanceAndRehydrate = createSelector(getContractsBalance, getRmsRehydrate, (contracts, rehydate) => {
  return {
    contracts,
    rehydate,
  };
});

const range = (n) => Array.from({ length: n }, (value, key) => key);

const isRehydratation = (
  id: number,
  rehydrateState: {
    [id: number]: {
      [key: string]: any;
    };
  },
  config: AddedValueFieldConfig,
  fieldName = config.name,
) => {
  if (!!rehydrateState && rehydrateState[id] && config.rehydrate) {
    const rehydratationValue = _safe(() => rehydrateState[id][fieldName]);
    return !!rehydratationValue || fieldName in rehydrateState[id];
  } else {
    return false;
  }
};

/**
 * Loads the product added values
 * This only applies the first time added values loads (To the first value added dropdown)
 */
@Injectable()
export class LoadProductAddedValuesNotFormulating implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}

  onFieldChanged(
    value: string,
    formGroup: UntypedFormGroup,
    form: ConfigurableFormComponent,
    config: AddedValueFieldConfig,
  ): void {
    this.store
      .pipe(
        select(balanceAndRehydrate),
        filter((data) => !!_safe(() => data.contracts[value])),
        take(1),
      )
      .subscribe((data) => {
        const productCode = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;
        const contract = _safe(() => data.contracts[value]);
        if (isRehydratation(form.data.id, data.rehydate, config, generateAddedValueFormName(0))) {
          this.store.dispatch(new RmsSpecificationRemoveRehydrateItem({ id: form.data.id, key: config.name }));
        } else {
          if (!!value && contract && contract.length) {
            this.store.dispatch(
              new LoadAddedValuesNotFormulatingAction({
                productCode: productCode,
                index: 0,
                contractCode: value,
              }),
            );
          }
        }
      });
  }
}

@Injectable()
export class LoadChildAddedValuesNotFormulating implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}

  private filterRehydrate(form: ConfigurableFormComponent, config: AddedValueFieldConfig, formGroup: UntypedFormGroup) {
    return this.store.pipe(
      select(balanceAndRehydrate),
      filter((data) => !!_safe(() => data.contracts[formGroup.get(SpecFieldNames.CONTRACT_SELECT).value])),
      take(1),
      filter((data) => {
        if (isRehydratation(form.data.id, data.rehydate, config)) {
          this.store.dispatch(new RmsSpecificationRemoveRehydrateItem({ id: form.data.id, key: config.name }));
          return false;
        } else {
          return true;
        }
      }),
    );
  }

  private accAddedValues(productId: any, formGroup: UntypedFormGroup, config: AddedValueFieldConfig) {
    return this.store.pipe(
      select(productAddedValues(productId)),
      filter((tuples) => tuples && !!tuples.length),
      take(1),
      map((tuples) => {
        // Accomulate added values
        return range(config.index + 1).map((_, index) => {
          const family = formGroup.get(generateAddedValueFormName(index)).value;
          if (tuples[index] && tuples[index].addedValues) {
            return tuples[index].addedValues.find((adddedValue) => adddedValue.family === family);
          } else {
            return null;
          }
        });
      }),
      filter((addedValue) => !!addedValue && !!addedValue.length),
    );
  }

  onFieldChanged(
    value: number,
    formGroup: UntypedFormGroup,
    form: ConfigurableFormComponent,
    config: AddedValueFieldConfig,
  ): void {
    this.filterRehydrate(form, config, formGroup).subscribe(() => {
      // Guard
      const productCode = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;
      const contractCode = formGroup.get(SpecFieldNames.CONTRACT_SELECT).value;

      if (config.limit || !productCode) {
        return;
      }

      // Case when unselecting first added value
      if (!value && config.index === 0 && !!productCode && !!contractCode) {
        this.store.dispatch(
          new LoadAddedValuesNotFormulatingAction({
            productCode: productCode,
            index: 0,
            contractCode: contractCode,
          }),
        );
        return;
      }

      // Get entire selected object
      this.accAddedValues(productCode, formGroup, config).subscribe((addedValues) => {
        if (productCode) {
          // Unselect next one to avoid auto-selection when fetching finishes
          const control = formGroup.get(generateAddedValueFormName(config.index + 1));
          if (control && control.value) {
            control.setValue(undefined);
          }
          const contractIdValue = formGroup.get(SpecFieldNames.CONTRACT_SELECT).value;
          if (!!contractIdValue && !!addedValues[0]) {
            this.store.dispatch(
              new LoadAddedValuesNotFormulatingAction({
                productCode: productCode,
                index: config.index + 1,
                addedValues: addedValues,
                contractCode: contractIdValue,
              }),
            );
          }
        }
      });
    });
  }
}
